import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import authUrl from '../../utils/authUrl';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare, Hash, Users, Send, Paperclip, X, ArrowLeft, Bookmark,
  Maximize2, Search, Ticket, Reply, Smile, Circle,
  File as FileIcon, Image as ImageIcon, Download,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useChatContext } from '../../contexts/ChatContext';
import EmojiPickerButton from '../EmojiPickerButton';
import VoiceMessagePlayer from './VoiceMessagePlayer';
import useDraggable from '../../hooks/useDraggable';
import { bubblePanelStyle } from '../../utils/bubblePanel';
import ImageLightbox from '../ImageLightbox';
import StickerShowcaseInline from '../StickerShowcaseInline';

/* ── Helpers ── */
function formatConvTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 604800000) {
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatMessageTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateSeparator(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

const QUICK_EMOJIS = ['👍', '❤️', '😂', '🎉', '😮', '🔥'];

export default function FloatingChat() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const chat = useChatContext();
  const {
    conversations, activeConversationId, selectConversation,
    messages, loadingMessages, hasMoreMessages, loadMore,
    typingUsers, onlineUsers, userStatuses,
    sendMessage, startTyping, stopTyping, markAsRead,
    toggleReaction, uploadFile, createConversation,
    totalUnread, floatingOpen, setFloatingOpen,
  } = chat;

  const { pos: dragPos, isDragging, wasDragged, handlers: dragHandlers, elementRef: fabRef } = useDraggable('floating-chat-pos', { defaultZone: 'bottom-right', width: 52, height: 52 });

  const [panelView, setPanelView] = useState('list'); // 'list' | 'messages'
  const [searchQ, setSearchQ] = useState('');
  const [msgInput, setMsgInput] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const isOnChatPage = location.pathname === '/chat';

  const prevMsgCount = useRef(0);
  const prevConvId = useRef(null);

  // Scroll: instant on conversation switch, smooth only for new messages when near bottom
  useEffect(() => {
    if (!floatingOpen || panelView !== 'messages' || messages.length === 0) return;
    const container = messagesContainerRef.current;
    const isConvSwitch = prevConvId.current !== activeConversationId;

    if (isConvSwitch) {
      // Conversation just loaded — jump instantly, no animation
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      prevConvId.current = activeConversationId;
    } else if (messages.length > prevMsgCount.current && container) {
      // New message arrived — only smooth-scroll if user is near bottom
      const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 120;
      if (nearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
    prevMsgCount.current = messages.length;
  }, [messages.length, floatingOpen, panelView, activeConversationId]);

  // When panel opens to a conversation, switch to messages view
  useEffect(() => {
    if (floatingOpen && activeConversationId) {
      setPanelView('messages');
    }
  }, [floatingOpen, activeConversationId]);

  // Conversation helpers
  // Abre "Guardado" (el chat con uno mismo) y lo crea la primera vez. Se ofrece siempre
  // como fila fija arriba de la lista: si esperáramos a que exista, nadie lo descubriría.
  const openSelfChat = async () => {
    const existing = conversations.find(c => c.is_self);
    if (existing) { selectConversation(existing.id); return; }
    try {
      const conv = await createConversation('dm', null, null, [user.id]);
      if (conv?.id) selectConversation(conv.id);
    } catch { toast.error(t('common.actionFailed')); }
  };

  const getConvName = (c) => {
    // "Guardado" es el DM de uno consigo mismo: no tiene otro miembro, así que dm_user
    // viene vacío y antes se habría mostrado como "DM".
    if (c.is_self) return t('chat.savedMessages');
    if (c.type === 'dm') return `${c.dm_user?.firstName || ''} ${c.dm_user?.lastName || ''}`.trim() || 'DM';
    return c.name || 'Sin nombre';
  };

  const getConvIcon = (c) => {
    if (c.is_self) return <Bookmark size={13} style={{ color: 'var(--c-brand)', flexShrink: 0 }} />;
    if (c.type === 'channel') return <Hash size={14} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />;
    if (c.type === 'group') return <Users size={13} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />;
    if (c.type === 'ticket_thread') return <Ticket size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />;
    // DM: user avatar circle
    return <Circle size={13} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />;
  };

  const activeConv = conversations.find(c => c.id === activeConversationId);

  const filteredConversations = searchQ
    ? conversations.filter(c => getConvName(c).toLowerCase().includes(searchQ.toLowerCase()))
    : conversations;

  // Send message handler
  const handleSend = useCallback(async () => {
    const text = msgInput.trim();
    if (!text || !activeConversationId) return;
    try {
      await sendMessage(activeConversationId, text, 'text', replyTo?.id);
      setMsgInput('');
      setReplyTo(null);
      stopTyping(activeConversationId);
    } catch (err) {
      console.error('Failed to send:', err);
    }
  }, [msgInput, activeConversationId, replyTo, sendMessage, stopTyping]);

  // File upload handler
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeConversationId) return;
    try {
      const uploaded = await uploadFile(file);
      const type = file.type.startsWith('image/') ? 'image' : 'file';
      await sendMessage(activeConversationId, uploaded.originalName || file.name, type, null, [uploaded]);
    } catch (err) {
      console.error('File upload failed:', err);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [activeConversationId, uploadFile, sendMessage]);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file && activeConversationId) {
          e.preventDefault();
          (async () => {
            try {
              const uploaded = await uploadFile(file);
              const type = file.type.startsWith('image/') ? 'image' : 'file';
              await sendMessage(activeConversationId, uploaded.originalName || file.name, type, null, [uploaded]);
            } catch (err) { console.error('Paste upload failed:', err); }
          })();
          return;
        }
      }
    }
  }, [activeConversationId, uploadFile, sendMessage]);

  // Key handler for textarea
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Select conversation from list
  const handleSelectConv = (convId) => {
    selectConversation(convId);
    setPanelView('messages');
  };

  // Expand to full chat page
  const handleExpand = () => {
    setFloatingOpen(false);
    if (activeConversationId) {
      navigate(`/chat?thread=${activeConversationId}`);
    } else {
      navigate('/chat');
    }
  };

  // Back to list
  const handleBack = () => {
    setPanelView('list');
  };

  // Close panel
  const handleClose = () => {
    setFloatingOpen(false);
  };

  // Typing indicator text
  const typingText = (() => {
    if (!activeConversationId) return null;
    const convTyping = typingUsers[activeConversationId] || {};
    const names = Object.values(convTyping);
    if (names.length === 0) return null;
    if (names.length === 1) return `${names[0]} escribiendo...`;
    return `${names.join(', ')} escribiendo...`;
  })();

  // Group messages by date
  const groupedMessages = (() => {
    const groups = [];
    let lastDate = null;
    messages.forEach(msg => {
      const msgDate = new Date(msg.created_at).toDateString();
      if (msgDate !== lastDate) {
        groups.push({ type: 'date', date: msg.created_at });
        lastDate = msgDate;
      }
      groups.push({ type: 'message', msg });
    });
    return groups;
  })();

  // Scroll to load more
  const handleScroll = (e) => {
    if (e.target.scrollTop === 0 && hasMoreMessages && !loadingMessages) {
      loadMore();
    }
  };

  // Hidden on /chat page
  if (isOnChatPage) return null;

  // FAB button — position always driven by snap-zone hook
  const fabStyle = {
    position: 'fixed', left: dragPos.x, top: dragPos.y,
    right: 'auto', bottom: 'auto',
    cursor: isDragging ? 'grabbing' : 'grab',
    transition: isDragging ? 'none' : 'left 0.18s ease, top 0.18s ease',
    touchAction: 'none',
  };

  const fab = (
    <button
      ref={fabRef}
      className="floating-chat-fab"
      style={fabStyle}
      onClick={() => { if (!wasDragged.current) setFloatingOpen(!floatingOpen); }}
      title={t('nav.chat')}
      {...dragHandlers}
    >
      {floatingOpen ? <X size={22} /> : <MessageSquare size={22} />}
      {!floatingOpen && totalUnread > 0 && (
        <span className="floating-chat-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
      )}
    </button>
  );

  // Panel — open relative to FAB, clamped to viewport
  // Misma cuenta que el resto de las burbujas (utils/bubblePanel): respeta barra lateral y encabezado.
  const panelStyle = bubblePanelStyle(dragPos, { width: 380, height: 480, minHeight: 300 });

  const panel = floatingOpen && (
    <div className={`floating-chat-panel ${floatingOpen ? 'floating-chat-panel-open' : ''}`} style={panelStyle}>
      {panelView === 'list' ? (
        /* ── LIST VIEW ── */
        <>
          <div className="floating-chat-header">
            <span style={{ fontWeight: 700, fontSize: 15 }}>Chat</span>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={handleExpand} title="Abrir chat completo">
                <Maximize2 size={14} />
              </button>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={handleClose}>
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Search */}
          <div style={{ padding: '0 12px 8px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-muted)' }} />
              <input
                className="input"
                placeholder={t('chat.searchConversations')}
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                style={{ width: '100%', paddingLeft: 28, fontSize: 12, height: 30 }}
              />
            </div>
          </div>

          {/* Conversations list */}
          <div className="floating-chat-list">
            {!searchQ && !conversations.some(c => c.is_self) && (
              <div
                className="floating-chat-conv"
                onClick={openSelfChat}
                title={t('chat.savedMessagesHint')}
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
              >
                <Bookmark size={13} style={{ color: 'var(--c-brand)', flexShrink: 0 }} />
                <span style={{ fontWeight: 600 }}>{t('chat.savedMessages')}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--c-text-muted)' }}>
                  {t('chat.savedMessagesHint')}
                </span>
              </div>
            )}
            {filteredConversations.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>
                {searchQ ? 'Sin resultados' : 'Sin conversaciones'}
              </div>
            ) : (
              filteredConversations.map(c => {
                const unread = parseInt(c.unread_count) || 0;
                const lastMsg = c.last_message;
                const isOnline = c.type === 'dm' && c.dm_user?.id && onlineUsers.has(c.dm_user.id);
                const uid = c.dm_user?.id;
                const st = uid && userStatuses?.[uid]?.status;

                return (
                  <div
                    key={c.id}
                    className={`floating-chat-conv-item ${c.id === activeConversationId ? 'active' : ''}`}
                    onClick={() => handleSelectConv(c.id)}
                  >
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      {getConvIcon(c)}
                      {c.type === 'dm' && (
                        <div style={{
                          position: 'absolute', bottom: -2, right: -2, width: 8, height: 8, borderRadius: '50%',
                          background: isOnline
                            ? (st === 'busy' ? '#ef4444' : st === 'dnd' ? '#6b7280' : st === 'away' ? '#f59e0b' : '#22c55e')
                            : '#6b7280',
                          border: '1.5px solid var(--c-surface)',
                        }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{
                          fontWeight: unread > 0 ? 700 : 500, fontSize: 12,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {getConvName(c)}
                        </span>
                        <span style={{ fontSize: 10, color: 'var(--c-text-muted)', flexShrink: 0, marginLeft: 4 }}>
                          {formatConvTime(lastMsg?.created_at || c.updated_at)}
                        </span>
                      </div>
                      {lastMsg && (
                        <div style={{
                          fontSize: 11, color: unread > 0 ? 'var(--c-text)' : 'var(--c-text-muted)',
                          fontWeight: unread > 0 ? 500 : 400,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {lastMsg.is_deleted ? 'Mensaje eliminado' : (
                            <>
                              {lastMsg.sender_name && <span>{lastMsg.sender_name}: </span>}
                              {lastMsg.type === 'voice' ? 'Audio' : lastMsg.content?.slice(0, 40)}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {unread > 0 && (
                      <span style={{
                        background: 'var(--c-brand)', color: '#fff', borderRadius: 10, padding: '0 6px',
                        fontSize: 10, fontWeight: 700, flexShrink: 0, minWidth: 18, textAlign: 'center', lineHeight: '18px',
                      }}>
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : (
        /* ── MESSAGES VIEW ── */
        <>
          <div className="floating-chat-header">
            <button className="btn btn-ghost btn-icon btn-sm" onClick={handleBack}>
              <ArrowLeft size={14} />
            </button>
            {activeConv && getConvIcon(activeConv)}
            <span style={{
              fontWeight: 600, fontSize: 13, flex: 1, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {activeConv ? getConvName(activeConv) : ''}
              {activeConv?.type === 'dm' && activeConv.dm_user?.id && (
                <StickerShowcaseInline userId={activeConv.dm_user.id} />
              )}
            </span>
            <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={handleExpand} title="Abrir chat completo">
                <Maximize2 size={14} />
              </button>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={handleClose}>
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            className="floating-chat-messages"
            ref={messagesContainerRef}
            onScroll={handleScroll}
          >
            {loadingMessages && messages.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 12 }}>
                Cargando...
              </div>
            )}
            {!loadingMessages && messages.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 12 }}>
                Sin mensajes aun
              </div>
            )}
            {groupedMessages.map((item, idx) => {
              if (item.type === 'date') {
                return (
                  <div key={`date-${idx}`} style={{
                    textAlign: 'center', fontSize: 10, color: 'var(--c-text-muted)',
                    padding: '8px 0 4px', fontWeight: 500,
                  }}>
                    {formatDateSeparator(item.date)}
                  </div>
                );
              }
              const msg = item.msg;
              const isMine = msg.sender_id === user.id;
              const isHovered = hoveredMsg === msg.id;
              const isDeleted = msg.is_deleted;

              return (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: isMine ? 'flex-end' : 'flex-start',
                    padding: '1px 12px', position: 'relative',
                  }}
                  onMouseEnter={() => setHoveredMsg(msg.id)}
                  onMouseLeave={() => setHoveredMsg(null)}
                >
                  {/* Sender name for group chats */}
                  {!isMine && activeConv?.type !== 'dm' && (
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--c-text-muted)', marginBottom: 1, paddingLeft: 2 }}>
                      {msg.sender_first_name || msg.sender_name || ''}
                    </span>
                  )}

                  {/* Reply preview */}
                  {msg.reply_preview && (
                    <div style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 6,
                      background: 'var(--c-surface-2)', borderLeft: '2px solid var(--c-brand)',
                      color: 'var(--c-text-muted)', marginBottom: 2, maxWidth: '85%',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {msg.reply_preview.isDeleted ? 'Mensaje eliminado' : msg.reply_preview.content?.slice(0, 50)}
                    </div>
                  )}

                  <div style={{
                    maxWidth: '85%', padding: '6px 10px', borderRadius: 12,
                    background: isDeleted ? 'transparent' : (isMine ? 'var(--c-brand)' : 'var(--c-surface-2)'),
                    color: isDeleted ? 'var(--c-text-muted)' : (isMine ? '#fff' : 'var(--c-text)'),
                    fontSize: 12, lineHeight: 1.4, wordBreak: 'break-word',
                    fontStyle: isDeleted ? 'italic' : 'normal',
                    position: 'relative',
                  }}>
                    {isDeleted ? 'Mensaje eliminado' : (
                      <>
                        {msg.type === 'voice' ? (
                          <VoiceMessagePlayer url={authUrl(msg.attachments?.[0]?.url)} mini />
                        ) : msg.type === 'image' ? (
                          <div>
                            {msg.attachments?.map((att, i) => {
                              const images = (msg.attachments || []).map(a => ({ src: authUrl(a.url), alt: a.originalName || 'image' }));
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => setLightbox({ images, startIndex: i })}
                                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'zoom-in', display: 'block', marginBottom: 2 }}
                                >
                                  <img
                                    src={authUrl(att.url)}
                                    alt={att.originalName || 'image'}
                                    style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, display: 'block' }}
                                  />
                                </button>
                              );
                            })}
                            {msg.content && <div style={{ marginTop: 2 }}>{msg.content}</div>}
                          </div>
                        ) : msg.type === 'file' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FileIcon size={14} />
                            <a
                              href={authUrl(msg.attachments?.[0]?.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: isMine ? '#fff' : 'var(--c-brand)', fontSize: 11, textDecoration: 'underline' }}
                            >
                              {msg.attachments?.[0]?.originalName || msg.content || 'File'}
                            </a>
                            <a href={authUrl(msg.attachments?.[0]?.url)} download style={{ color: isMine ? '#fff' : 'var(--c-text-muted)' }}>
                              <Download size={12} />
                            </a>
                          </div>
                        ) : (
                          <span style={{ whiteSpace: 'pre-wrap' }}>{(() => {
                            // Linkify URLs and phone numbers
                            const re = /(https?:\/\/[^\s]+)|(\+?\d[\d\s\-().]{7,}\d)/g;
                            const parts = []; let last = 0; let m;
                            while ((m = re.exec(msg.content)) !== null) {
                              if (m.index > last) parts.push(msg.content.slice(last, m.index));
                              const ls = { color: isMine ? '#fff' : 'var(--c-brand)', textDecoration: 'underline' };
                              if (m[1]) parts.push(<a key={m.index} href={m[1]} target="_blank" rel="noopener noreferrer" style={ls}>{m[1]}</a>);
                              else { const raw = m[2].replace(/[\s\-().]/g, ''); parts.push(<a key={m.index} href={`tel:${raw}`} style={ls}>{m[2]}</a>); }
                              last = m.index + m[0].length;
                            }
                            if (!parts.length) return msg.content;
                            if (last < msg.content.length) parts.push(msg.content.slice(last));
                            return parts;
                          })()}</span>
                        )}
                      </>
                    )}
                    <div style={{
                      fontSize: 9, opacity: 0.7, textAlign: 'right', marginTop: 2,
                      color: isDeleted ? 'var(--c-text-muted)' : undefined,
                    }}>
                      {formatMessageTime(msg.created_at)}
                      {msg.is_edited && <span style={{ marginLeft: 3, fontStyle: 'italic' }}>(editado)</span>}
                    </div>
                  </div>

                  {/* Reactions */}
                  {msg.reactions?.length > 0 && (
                    <div style={{ display: 'flex', gap: 3, marginTop: 1, flexWrap: 'wrap' }}>
                      {Object.entries(msg.reactions.reduce((acc, r) => {
                        acc[r.emoji] = (acc[r.emoji] || 0) + 1; return acc;
                      }, {})).map(([emoji, count]) => (
                        <span
                          key={emoji}
                          onClick={() => toggleReaction(msg.id, emoji)}
                          style={{
                            fontSize: 11, padding: '0 4px', borderRadius: 8, cursor: 'pointer',
                            background: 'var(--c-surface-2)', border: '1px solid var(--c-border)',
                          }}
                        >
                          {emoji} {count > 1 ? count : ''}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Hover actions */}
                  {isHovered && !isDeleted && (
                    <div style={{
                      position: 'absolute', top: 0, [isMine ? 'left' : 'right']: 8,
                      display: 'flex', gap: 2, background: 'var(--c-surface)', borderRadius: 6,
                      border: '1px solid var(--c-border)', padding: '1px 2px',
                    }}>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        style={{ width: 22, height: 22, padding: 0 }}
                        onClick={() => setShowEmojiPicker(showEmojiPicker === msg.id ? null : msg.id)}
                      >
                        <Smile size={11} />
                      </button>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        style={{ width: 22, height: 22, padding: 0 }}
                        onClick={() => setReplyTo(msg)}
                      >
                        <Reply size={11} />
                      </button>
                    </div>
                  )}

                  {/* Emoji quick-picker */}
                  {showEmojiPicker === msg.id && (
                    <div style={{
                      display: 'flex', gap: 2, padding: '3px 6px', borderRadius: 8,
                      background: 'var(--c-surface)', border: '1px solid var(--c-border)',
                      marginTop: 2,
                    }}>
                      {QUICK_EMOJIS.map(e => (
                        <span
                          key={e}
                          onClick={() => { toggleReaction(msg.id, e); setShowEmojiPicker(null); }}
                          style={{ cursor: 'pointer', fontSize: 14, padding: '0 2px' }}
                        >
                          {e}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Typing indicator */}
            {typingText && (
              <div style={{ padding: '4px 12px', fontSize: 11, color: 'var(--c-text-muted)', fontStyle: 'italic' }}>
                {typingText}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Reply bar */}
          {replyTo && (
            <div style={{
              padding: '4px 12px', borderTop: '1px solid var(--c-border)',
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 11,
              color: 'var(--c-text-muted)', background: 'var(--c-surface-2)',
            }}>
              <Reply size={11} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {replyTo.sender_first_name}: {replyTo.content?.slice(0, 40)}
              </span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setReplyTo(null)} style={{ width: 18, height: 18, padding: 0 }}>
                <X size={11} />
              </button>
            </div>
          )}

          {/* Input bar */}
          <div className="floating-chat-input">
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />
            <button
              className="btn btn-ghost btn-icon btn-sm"
              onClick={() => fileInputRef.current?.click()}
              style={{ width: 28, height: 28, padding: 0, flexShrink: 0 }}
            >
              <Paperclip size={14} />
            </button>
            <EmojiPickerButton
              size={14}
              onEmojiSelect={(emoji) => { setMsgInput(prev => prev + emoji); textareaRef.current?.focus(); }}
              buttonStyle={{ width: 28, height: 28, padding: 0, flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--c-text-muted)' }}
            />
            <textarea
              ref={textareaRef}
              className="input"
              placeholder={t('chat.typeMessage')}
              value={msgInput}
              onChange={e => {
                setMsgInput(e.target.value);
                if (activeConversationId) startTyping(activeConversationId);
              }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              rows={1}
              style={{
                flex: 1, resize: 'none', fontSize: 12, minHeight: 28, maxHeight: 80,
                padding: '4px 8px', borderRadius: 8, lineHeight: 1.4,
              }}
            />
            <button
              className="btn btn-primary btn-icon btn-sm"
              onClick={handleSend}
              disabled={!msgInput.trim()}
              style={{ width: 28, height: 28, padding: 0, flexShrink: 0, borderRadius: 8 }}
            >
              <Send size={13} />
            </button>
          </div>
        </>
      )}
    </div>
  );

  return ReactDOM.createPortal(
    <>
      {fab}
      {panel}
      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          startIndex={lightbox.startIndex}
          onClose={() => setLightbox(null)}
          onSendEdited={activeConversationId ? async (blob, fileName) => {
            const file = new File([blob], fileName, { type: blob.type || 'image/png' });
            const uploaded = await uploadFile(file);
            await sendMessage(activeConversationId, uploaded.originalName || fileName, 'image', null, [uploaded]);
          } : undefined}
        />
      )}
    </>,
    document.body
  );
}
